/**
 * swal-compat.ts — Wrapper de compatibilidade para substituir sweetalert2
 *
 * Este módulo exporta um objeto `Swal` com a mesma API que o sweetalert2,
 * mas delega internamente para o SvMessageService nativo.
 *
 * UTILIZAÇÃO (substituição direta):
 *   Antes:  import Swal from 'sweetalert2';
 *   Depois: import Swal from 'src/app/shared/services/swal-compat';
 *
 * A lógica de negócio NÃO muda — apenas o import.
 *
 * Propriedades SweetAlert2 suportadas:
 *  - title, text, html, icon/type
 *  - showCancelButton, cancelButtonText, confirmButtonText
 *  - showConfirmButton, reverseButtons, focusCancel
 *  - timer, toast, position
 *  - imageUrl, imageWidth
 *
 * Propriedades SweetAlert2 ignoradas (geridas pelo CSS do SvMessage):
 *  - confirmButtonColor, cancelButtonColor, customClass
 *  - width, focusConfirm, preConfirm, didOpen, didClose
 *  - showValidationMessage (não suportado)
 */
import { SvMessageService } from './sv-message.service';
import { SvMessageConfig, SvMessageType, SvMessageResult } from '../components/sv-message/sv-message.component';

/**
 * Mapeia as propriedades de configuração do SweetAlert2 para o SvMessageConfig.
 */
function mapSwalConfig(swalConfig: Record<string, any>): Partial<SvMessageConfig> {
  const config: Partial<SvMessageConfig> = {};

  // Mapear icon → type
  if (swalConfig.icon && !swalConfig.type) {
    config.type = swalConfig.icon as SvMessageType;
  } else if (swalConfig.type) {
    config.type = swalConfig.type as SvMessageType;
  }

  // Propriedades directamente compatíveis
  if (swalConfig.title !== undefined) config.title = swalConfig.title;
  if (swalConfig.text !== undefined) config.text = swalConfig.text;
  if (swalConfig.html !== undefined) config.html = swalConfig.html;
  if (swalConfig.showCancelButton !== undefined) config.showCancelButton = swalConfig.showCancelButton;
  if (swalConfig.cancelButtonText !== undefined) config.cancelButtonText = swalConfig.cancelButtonText;
  if (swalConfig.confirmButtonText !== undefined) config.confirmButtonText = swalConfig.confirmButtonText;
  if (swalConfig.showConfirmButton !== undefined) config.showConfirmButton = swalConfig.showConfirmButton;
  if (swalConfig.reverseButtons !== undefined) config.reverseButtons = swalConfig.reverseButtons;
  if (swalConfig.focusCancel !== undefined) config.focusCancel = swalConfig.focusCancel;
  if (swalConfig.timer !== undefined) config.timer = swalConfig.timer;
  if (swalConfig.imageUrl !== undefined) config.imageUrl = swalConfig.imageUrl;
  if (swalConfig.imageWidth !== undefined) config.imageWidth = swalConfig.imageWidth;

  // Toast + posição
  if (swalConfig.toast !== undefined) config.toast = swalConfig.toast;
  if (swalConfig.position) {
    config.toastPosition = swalConfig.position;
  }

  // Input de texto
  if (swalConfig.input === 'text') {
    config.input = 'text';
    if (swalConfig.inputValue !== undefined) config.inputValue = swalConfig.inputValue;
    if (swalConfig.inputPlaceholder !== undefined) config.inputPlaceholder = swalConfig.inputPlaceholder;
  }

  // Propriedades ignoradas (não suportadas pelo SvMessage, geridas pelo CSS):
  // confirmButtonColor, cancelButtonColor, customClass, width,
  // focusConfirm, preConfirm, didOpen, didClose, inputOptions, etc.

  return config;
}

/**
 * Resultado compatível com SweetAlert2.
 * O SweetAlert2 retorna { isConfirmed, isDismissed, isDenied, value }.
 * O SvMessage retorna { isConfirmed, isDismissed }.
 * Adicionamos `value` para compatibilidade com .then(result => result.value)
 */
function mapResult(result: SvMessageResult): SvMessageResult & { value?: any; isDenied?: boolean } {
  return {
    ...result,
    value: result.isConfirmed ? result.value || true : undefined,
    isDenied: false,
  };
}

/**
 * Wrapper de compatibilidade Swal.
 */
const Swal = {
  /**
   * fire() — API compatível com SweetAlert2.
   *
   * Aceita as mesmas 3 assinaturas:
   *  1. fire(config: object)
   *  2. fire(title, text, icon)
   *  3. fire(title) — só título
   */
  fire(
    titleOrConfig?: string | Record<string, any>,
    text?: string,
    icon?: string,
  ): Promise<SvMessageResult & { value?: any; isDenied?: boolean }> {
    const svc = SvMessageService.instance;
    if (!svc) {
      console.warn('[SwalCompat] SvMessageService ainda não foi inicializado. A mensagem será ignorada.');
      return Promise.resolve({ isConfirmed: true, isDismissed: false, value: true as any, isDenied: false });
    }

    // Assinatura 1: fire(config)
    if (typeof titleOrConfig === 'object' && titleOrConfig !== null) {
      const config = mapSwalConfig(titleOrConfig);
      return svc.fire(config).then(mapResult);
    }

    // Assinatura 2: fire(title, text, icon) ou fire(title)
    if (typeof titleOrConfig === 'string') {
      return svc.fire(titleOrConfig, text, (icon as SvMessageType) || undefined).then(mapResult);
    }

    // Fallback
    return svc.fire({ type: 'info', title: '' }).then(mapResult);
  },

  /**
   * showValidationMessage() — stub de compatibilidade.
   * Não suportado pelo SvMessage, log de warning para debug.
   */
  showValidationMessage(message: string): void {
    console.warn('[SwalCompat] showValidationMessage não suportado:', message);
  },

  /**
   * isLoading() — stub de compatibilidade.
   * Devolve sempre false (o SvMessage não tem estado de loading).
   */
  isLoading(): boolean {
    return false;
  },

  /**
   * close() — fecha o diálogo activo.
   */
  close(): void {
    console.warn('[SwalCompat] close() chamado — sem efeito no SvMessage.');
  },
};

export default Swal;
